import {
  IsArray,
  IsEmail,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
} from 'class-validator'
import { ApiPropertyOptional } from '@nestjs/swagger'
import {
  MAX_ESTABLISHMENT_NAME_LENGTH,
  MAX_SHORT_NAME_LENGTH,
  UAI_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_STREET_LENGTH,
  MAX_CITY_LENGTH,
  MAX_POSTAL_CODE_LENGTH,
} from '../../../constants/establishment.constants'
import {
  EstablishmentCategory,
  EstablishmentStatus,
} from '../../../enums/content.enum'

export class UpdateEstablishmentDto {
  @ApiPropertyOptional({ description: "Nom de l'établissement", type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_ESTABLISHMENT_NAME_LENGTH)
  name?: string

  @ApiPropertyOptional({ description: 'Nom court', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_SHORT_NAME_LENGTH)
  shortName?: string

  @ApiPropertyOptional({ description: 'Code UAI', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(UAI_LENGTH)
  uai?: string

  @ApiPropertyOptional({ description: 'ID du type', type: 'string' })
  @IsOptional()
  @IsUUID('4')
  typeId?: string

  @ApiPropertyOptional({ description: 'Catégorie' })
  @IsOptional()
  @IsString()
  category?: EstablishmentCategory

  @ApiPropertyOptional({ description: 'Statut' })
  @IsOptional()
  @IsString()
  status?: EstablishmentStatus

  @ApiPropertyOptional({ description: 'Filières', type: [String] })
  @IsOptional()
  @IsArray()
  streams?: string[]

  @ApiPropertyOptional({ description: 'Email', type: 'string' })
  @IsOptional()
  @IsEmail()
  @MaxLength(MAX_EMAIL_LENGTH)
  email?: string

  @ApiPropertyOptional({ description: 'Téléphone', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_PHONE_LENGTH)
  phone?: string

  @ApiPropertyOptional({ description: 'Site web', type: 'string' })
  @IsOptional()
  @IsString()
  website?: string

  @ApiPropertyOptional({ description: 'Rue', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_STREET_LENGTH)
  street?: string

  @ApiPropertyOptional({ description: "Complément d'adresse", type: 'string' })
  @IsOptional()
  @IsString()
  addressComplement?: string

  @ApiPropertyOptional({ description: 'Ville', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_CITY_LENGTH)
  city?: string

  @ApiPropertyOptional({ description: 'Code postal', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_POSTAL_CODE_LENGTH)
  postalCode?: string

  @ApiPropertyOptional({ description: 'Département', type: 'string' })
  @IsOptional()
  @IsString()
  department?: string

  @ApiPropertyOptional({ description: 'Région', type: 'string' })
  @IsOptional()
  @IsString()
  region?: string

  @ApiPropertyOptional({ description: 'Académie', type: 'string' })
  @IsOptional()
  @IsString()
  academy?: string

  // --- Nouveaux champs enrichis ---

  @ApiPropertyOptional({ description: 'Code nature', type: 'string' })
  @IsOptional()
  @IsString()
  natureCode?: string

  @ApiPropertyOptional({ description: 'Libellé nature', type: 'string' })
  @IsOptional()
  @IsString()
  natureLabel?: string

  @ApiPropertyOptional({ description: 'A une SEGPA', type: Boolean })
  @IsOptional()
  hasSegpa?: boolean

  @ApiPropertyOptional({ description: 'A une ULIS', type: Boolean })
  @IsOptional()
  hasUlis?: boolean

  @ApiPropertyOptional({ description: 'Section Arts', type: Boolean })
  @IsOptional()
  sectionArts?: boolean

  @ApiPropertyOptional({ description: 'Section Cinéma', type: Boolean })
  @IsOptional()
  sectionCinema?: boolean

  @ApiPropertyOptional({ description: 'Section Théâtre', type: Boolean })
  @IsOptional()
  sectionTheatre?: boolean

  @ApiPropertyOptional({ description: 'Section Sport', type: Boolean })
  @IsOptional()
  sectionSport?: boolean

  @ApiPropertyOptional({ description: 'Section Internat', type: Boolean })
  @IsOptional()
  sectionInternat?: boolean

  @ApiPropertyOptional({ description: 'Section Internationale', type: Boolean })
  @IsOptional()
  sectionInternationale?: boolean

  @ApiPropertyOptional({ description: 'Section Européenne', type: Boolean })
  @IsOptional()
  sectionEuropeenne?: boolean

  @ApiPropertyOptional({ description: 'Restauration', type: Boolean })
  @IsOptional()
  restauration?: boolean

  @ApiPropertyOptional({ description: 'Hébergement', type: Boolean })
  @IsOptional()
  hebergement?: boolean

  @ApiPropertyOptional({
    description:
      "Effectif déclaré (donnée informative saisie manuellement, distincte du nombre réel de membres inscrits)",
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  numberOfStudents?: number

  @ApiPropertyOptional({ description: 'UAI Parent', type: 'string' })
  @IsOptional()
  @IsString()
  parentUai?: string

  @ApiPropertyOptional({ description: 'Type Parent', type: 'string' })
  @IsOptional()
  @IsString()
  parentType?: string

  @ApiPropertyOptional({ description: 'Type contrat privé', type: 'string' })
  @IsOptional()
  @IsString()
  privateContractType?: string

  @ApiPropertyOptional({ description: 'Code contrat privé', type: 'string' })
  @IsOptional()
  @IsString()
  privateContractCode?: string

  @ApiPropertyOptional({ description: 'Code circonscription', type: 'string' })
  @IsOptional()
  @IsString()
  districtCode?: string

  @ApiPropertyOptional({ description: 'Libellé circonscription', type: 'string' })
  @IsOptional()
  @IsString()
  districtLabel?: string

  @ApiPropertyOptional({ description: 'Code bassin formation', type: 'string' })
  @IsOptional()
  @IsString()
  formationBasinCode?: string

  @ApiPropertyOptional({ description: 'Libellé bassin formation', type: 'string' })
  @IsOptional()
  @IsString()
  formationBasinLabel?: string

  @ApiPropertyOptional({ description: "Date d'ouverture" })
  @IsOptional()
  openedAt?: Date

  @ApiPropertyOptional({ description: 'Date MAJ officielle' })
  @IsOptional()
  officialUpdatedAt?: Date

  @ApiPropertyOptional({ description: 'Statut officiel', type: 'string' })
  @IsOptional()
  @IsString()
  officialStatus?: string

  @ApiPropertyOptional({ description: 'Ministère de tutelle', type: 'string' })
  @IsOptional()
  @IsString()
  ministry?: string

  @ApiPropertyOptional({ description: 'Latitude', type: Number })
  @IsOptional()
  @IsNumber()
  latitude?: number

  @ApiPropertyOptional({ description: 'Longitude', type: Number })
  @IsOptional()
  @IsNumber()
  longitude?: number
}
